import { Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DwContextSubjects, DwMetaDataFormComponent, DwMetaDataFormConfig, DwSectionComponent } from '@devwareapps/devware-cap';
import { combineLatest, Subscription } from 'rxjs';
import { DwUserEntity } from '../../../../meta-data/app-meta-data.service';
import { ProfileFormService } from '../../../user-profile/services/profile-form.service';
import { ProfileInlineEditService } from '../../../user-profile/services/profile-inline-edit.service';
import { createInlineProfileEditPreferences } from '../../../user-profile/user-profile.config';

/** CAP section container: the banner stays outside, while its content turns into an edit form. */
@DwSectionComponent({ keyName: 'fitnessProfileFlip', display: 'Profile view/edit panel', sectionIcon: 'fa fa-user-edit',
    hasChildren: true, childrenPropertyName: 'sections' })
@Component({ selector: 'app-profile-flip-panel', template: `
  <div class="flip-panel" [class.editing]="editing">
    <div class="flip-faces">
      <div class="flip-face flip-front" [attr.aria-hidden]="editing" [attr.inert]="editing ? '' : null">
        <dw-dynamic-section *ngFor="let section of sectionConfig.sections" [sectionConfig]="section"
          [context]="contexts" [formConfig]="formConfig" [formGroup]="formGroup"
          [submitted]="submitted" [parentIsVisible]="parentIsVisible && visible && !editing"></dw-dynamic-section>
      </div>
      <section class="flip-face flip-back" [attr.aria-hidden]="!editing" [attr.inert]="editing ? null : ''">
        <h2 tabindex="-1">Edit profile</h2>
        <dw-meta-data-form *ngIf="renderEditor" #editor item="dw_User" [keyValue]="userId"
          configKeyPart="participant-profile-edit" [preferences]="editPreferences" [allowConfiguration]="true"
          [handleRedirect]="false" (returnToPrevious)="finish($event)"></dw-meta-data-form>
      </section>
    </div>
  </div>`, styleUrls: ['./profile-flip-panel.component.scss'] })
export class ProfileFlipPanelComponent implements OnDestroy {
    @Input() sectionConfig: { sections: any[] };
    @Input() formConfig: DwMetaDataFormConfig;
    @Input() formGroup: FormGroup;
    @Input() submitted = false;
    @Input() parentIsVisible = true;
    @Input() visible = true;
    contexts: DwContextSubjects;
    userId: number;
    editing = false;
    renderEditor = false;
    editPreferences = createInlineProfileEditPreferences();
    private _subscription = new Subscription();
    private _focusTimer: ReturnType<typeof setTimeout>;
    private _transitionTimer: ReturnType<typeof setTimeout>;
    private _editor: DwMetaDataFormComponent;
    constructor(public inline: ProfileInlineEditService, private forms: ProfileFormService,
        private _element: ElementRef<HTMLElement>) {}
    @ViewChild('editor') set editor(value: DwMetaDataFormComponent) {
        this._editor = value;
        if (this.editing) { this.inline.form = value; }
    }
    @Input() set context(value: DwContextSubjects) {
        this.contexts = value;
        this._subscription.unsubscribe(); this._subscription = new Subscription();
        if (!value?.data) { return; }
        this._subscription.add(combineLatest([value.data, this.inline.userId$]).subscribe(([data, editingId]) => {
            this.userId = data?.UserId;
            const editing = editingId === this.userId && this.forms.canEdit(this.userId);
            if (editing !== this.editing) {
                this.editing = editing;
                clearTimeout(this._focusTimer);
                clearTimeout(this._transitionTimer);
                if (editing) { this.renderEditor = true; this._focusTimer = setTimeout(() => this._element.nativeElement.querySelector<HTMLElement>('h2')?.focus(), 350); }
                else { this._transitionTimer = setTimeout(() => this.renderEditor = false, 350); }
            }
        }));
    }
    finish(cancelled: boolean): void {
        // CAP emits returnToPrevious only after a successful save or its Cancel action.
        const data = cancelled ? undefined : this._editor?.getFormData() as DwUserEntity;
        this.inline.finish(this.userId, cancelled, data);
    }
    ngOnDestroy(): void {
        clearTimeout(this._focusTimer); clearTimeout(this._transitionTimer); this._subscription.unsubscribe();
        if (this.inline.userId$.value === this.userId) { this.inline.finish(this.userId, true); }
    }
}
